import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

type Language = {
  code: string;
  name: string;
  flag: string;
};

export const languages: Language[] = [
  { code: 'english', name: 'English', flag: '🇬🇧' },
  { code: 'french', name: 'French', flag: '🇫🇷' },
  { code: 'german', name: 'German', flag: '🇩🇪' },
  { code: 'spanish', name: 'Spanish', flag: '🇪🇸' },
  { code: 'italian', name: 'Italian', flag: '🇮🇹' },
  { code: 'swedish', name: 'Swedish', flag: '🇸🇪' },
  { code: 'dutch', name: 'Dutch', flag: '🇳🇱' },
];

const DEFAULT_LANGUAGE = languages[0]; // English

type LanguageContextType = {
  currentLanguage: Language;
  setLanguage: (language: Language) => void;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage) {
      const parsed = JSON.parse(savedLanguage);
      const found = languages.find(lang => lang.code === parsed.code);
      return found || DEFAULT_LANGUAGE;
    }
    return DEFAULT_LANGUAGE;
  });

  useEffect(() => {
    localStorage.setItem('preferredLanguage', JSON.stringify(currentLanguage));
  }, [currentLanguage]);

  const setLanguage = (language: Language) => {
    setCurrentLanguage(language);
    // TODO: Implement language change logic (i18n)
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
